<template>
    <div>
        <r-button @click="openModal">Preview Email</r-button>
        <v-modal
            v-if="isModalVisible"
            :scroll="true"
            size="extra-large"
            title="Preview Email"
            cancelLabel="Close"
            @close="closeModal">
            <div slot="contents" class="position-relative">
                <v-inline-loader v-if="isLoading"/>
                <div class="modal-content">
                    <p class="stack-m">You can change the <strong>from</strong>, <strong>cc</strong> and <strong>bcc</strong> fields before sending the preview email. The same credentials and sender information you defined for the original email will be used.</p>
                    <p>Optionally you can modify placeholders with your own data or leave them as-is to test the content's rendering with real data.</p>
                </div>
                <div class="modal-content">
                    <r-tabs :divided="true">
                        <r-tab name="Email Recipients" :class="{'has-error': $v.preview.$error}">
                            <v-error-messages
                                :has-frontend-errors="$v.preview.$error"
                                :server-error="serverError"
                                class="stack-m">
                                <li v-if="!$v.preview.from.required">Please provide an email address for the "from" field.</li>
                                <li v-if="!$v.preview.to.required">Please provide at least one email address for the "to" field.</li>
                                <li v-if="!$v.preview.from.email">Please provide a valid email address for the "from" field.</li>
                                <li v-if="!$v.preview.to.email">Please provide valid email addresses for the "to" field.</li>
                            </v-error-messages>
                            <div class="grid-content">
                                <div class="grid-item grid-item-full">
                                    <r-input
                                        label="From"
                                        v-model="preview.from"
                                        :validate="$v.preview.from"/>
                                </div>
                                <div class="grid-item grid-item-full">
                                    <v-select
                                        :class="{'stack-s': !ccEnabled || !bccEnabled}"
                                        label="To"
                                        placeholder="Type an email address"
                                        :multiple="true"
                                        :taggable="true"
                                        :tag-validator="validateTag"
                                        :allow-empty="true"
                                        v-model="preview.to"
                                        :validate="$v.preview.to"
                                        :options="toPlaceholders"/>
                                    <div class="grid-content grid-fitted" v-if="!ccEnabled || !bccEnabled">
                                        <div class="grid-item">&nbsp;</div>
                                        <div class="grid-item no-flex" v-if="!ccEnabled">
                                            <r-button type="link" @click="enableCopy" class="inline-s">Add CC</r-button>
                                        </div>
                                        <div class="grid-item no-flex" v-if="!bccEnabled">
                                            <r-button type="link" @click="enableBlindCopy">Add BCC</r-button>
                                        </div>
                                    </div>
                                </div>
                                <div class="grid-item grid-item-full" v-if="ccEnabled">
                                    <v-select
                                        label="CC"
                                        placeholder="Type an email address"
                                        :multiple="true"
                                        :taggable="true"
                                        :tag-validator="validateTag"
                                        :allow-empty="true"
                                        v-model="preview.cc"
                                        :validate="$v.preview.cc"
                                        :options="toPlaceholders"/>
                                </div>
                                <div class="grid-item grid-item-full" v-if="bccEnabled">
                                    <v-select
                                        label="BCC"
                                        placeholder="Type an email address"
                                        :multiple="true"
                                        :taggable="true"
                                        :tag-validator="validateTag"
                                        :allow-empty="true"
                                        v-model="preview.bcc"
                                        :validate="$v.preview.bcc"
                                        :options="toPlaceholders"/>
                                </div>
                            </div>
                        </r-tab>
                        <r-tab name="HTML Placeholders">
                            <div class="grid-content">
                                <div v-if="!hasHTMLPlaceholders" class="grid-item grid-item-full">
                                    <p>No placeholders defined for the HTML content.</p>
                                </div>
                                <div v-else
                                     class="grid-item grid-item-half"
                                     v-for="(item, index) in placeholders.html"
                                     :key="index">
                                    <r-input
                                        :label="item.label"
                                        :value="item.value"
                                        @input="value => updatePlaceholder(placeholders.html, item.token, value)"/>
                                </div>
                            </div>
                        </r-tab>
                        <r-tab name="Text Placeholders">
                            <div class="grid-content">
                                <div v-if="!hasTextPlaceholders" class="grid-item grid-item-full">
                                    <p>No placeholders defined for the fallback content.</p>
                                </div>
                                <div v-else
                                     class="grid-item grid-item-half"
                                     v-for="(item, index) in placeholders.text"
                                     :key="index">
                                    <r-input
                                        :label="item.label"
                                        :value="item.value"
                                        @input="value => updatePlaceholder(placeholders.text, item.token, value)"/>
                                </div>
                            </div>
                        </r-tab>
                    </r-tabs>
                </div>
                <div class="modal-content">
                    <h3 class="stack-m">Live Content Preview</h3>
                    <p class="stack-m">Below you can preview how the email will look when viewed by the recipients. The HTML content will be displayed wherever rich content is supported otherwise the fallback text will be used instead.</p>
                    <p class="stack-m">Click <strong>Send Email</strong> to send a live email to the recipients.</p>
                    <r-tabs :divided="true">
                        <r-tab name="HTML">
                            <iframe
                                v-if="isModalVisible"
                                frameborder="0"
                                allowtransparency="true"
                                sandbox="allow-same-origin"
                                :srcdoc="liveHTMLPreview"
                                scrolling="no"
                                @load="resizeBodyIFrame"
                                class="preview-notification-modal-body-html">
                            </iframe>
                        </r-tab>
                        <r-tab name="Text (fallback)">
                            <div class="preview-notification-modal-body-html" style="padding:1em;">
                                {{liveTextPreview}}
                            </div>
                        </r-tab>
                    </r-tabs>
                </div>
            </div>
            <template slot="actions">
                <r-button
                    :type="isLoading ? 'disabled' : 'primary'"
                    :disabled="isLoading"
                    @click="testEmail">Send Email
                </r-button>
            </template>
        </v-modal>
    </div>
</template>

<script>
    import {
        vSelect, vErrorMessages,
    } from '@/components/form';
    import matchAll from '@/common/helpers/match-all';
    import _ from '@/common/helpers';
    import vModal from '@/components/modal/v-modal';
    import {required, email} from 'vuelidate/lib/validators';
    import placeholderPattern from '@/components/webhook/placeholder-pattern';
    import EmailCredentialModel from '@/common/models/email-credential-model';
    import vInlineLoader from '@/components/v-inline-loader';
    import getCredentialDelegate from '@/modules/rules-engine/actions/send-email/components/credentials/get-credential-delegate';

    export default {
        name: 'preview-email',
        components: {
            vModal,
            vSelect,
            vErrorMessages,
            vInlineLoader,
        },
        props: {
            /**
             * @type {EmailModel|{}}
             */
            email: Object,
            beforePreview: Function,
        },
        computed: {
            liveHTMLPreview() {
                return this.placeholders.html
                    .reduce(
                        (result, item) => result.replace(item.token, item.value),
                        this.preview.html,
                    );
            },
            liveTextPreview() {
                return this.placeholders.text
                    .reduce(
                        (result, item) => result.replace(item.token, item.value),
                        this.preview.text,
                    );
            },
            hasHTMLPlaceholders() {
                return this.placeholders.html.length > 0;
            },
            hasTextPlaceholders() {
                return this.placeholders.text.length > 0;
            },
        },
        validations: {
            preview: {
                from: {
                    required,
                    email,
                },
                to: {
                    required,
                    $each: {
                        email,
                    },
                },
                cc: {
                    $each: {
                        email,
                    },
                },
                bcc: {
                    $each: {
                        email,
                    },
                },
            },
        },
        watch: {
            email: {
                handler(newEmail) {
                    this.preview = newEmail.cloneSelf();
                    if (newEmail.$credentials !== null) {
                        // copy credentials from parent
                        const credentials = newEmail.$credentials.getPayload();
                        credentials.type = newEmail.$credentials.type;
                        this.preview.$credentials = new EmailCredentialModel(credentials);
                    }
                    if (!email(this.preview.from)) {
                        this.preview.from = null;
                    }
                    this.placeholders = {
                        html: this.makePlaceholders(newEmail.html),
                        text: this.makePlaceholders(newEmail.text),
                    };
                    // restore cc/bcc flags
                    this.ccEnabled = newEmail.cc.length;
                    this.bccEnabled = newEmail.bcc.length;
                    this.toPlaceholders = newEmail.receivers
                        .map(value => ({value, label: value}));
                },
                immediate: true,
            },
        },
        data() {
            return {
                preview: null,
                isModalVisible: false,
                isLoading: false,
                placeholders: null,
                toPlaceholders: null,
                ccEnabled: false,
                bccEnabled: false,
                serverError: null,
            };
        },
        methods: {
            openModal() {
                if (this.beforePreview()) {
                    this.isModalVisible = true;
                } else {
                    this.$toast.negative('Please correct any validation issues before trying to preview the email.', {delay: 5000});
                }
            },
            closeModal() {
                if (!this.isLoading) {
                    this.isModalVisible = false;
                }
            },
            resizeBodyIFrame({target: iFrame}) {
                const contentHeightCorrection = 32;
                const iFrameBody = iFrame.contentWindow.document.body;
                iFrame.style.height = `${iFrameBody.scrollHeight + contentHeightCorrection}px`;
            },
            validateTag(tag) {
                const valid = email(tag);
                if (!valid) {
                    this.$toast.negative(`"${tag}" is not a email address.`, {delay: 5000});
                }
                return valid;
            },
            async testEmail() {
                this.$v.preview.$touch();
                if (!this.$v.preview.$error) {
                    this.isLoading = true;
                    const data = this.preview.getPayload();
                    if (this.preview.$credentials !== null) {
                        try {
                            const {fields} = await this.$api(
                                this.getCredentialDelegate(
                                    this.preview.$credentials.getPayload(),
                                    this.preview.$credentials,
                                ),
                            );
                            // update email hash with new credential value
                            data.credentialHash = fields.hash;
                        } catch (credError) {
                            this.$toast.negative('Your email credentials seem to be invalid.');
                            this.isLoading = false;
                            return;
                        }
                    }
                    data.html = this.liveHTMLPreview;
                    data.text = this.liveTextPreview;
                    try {
                        await this.$api(({previews}) => previews.sendEmailRuleAction({data}));
                        const recipients = [].concat(this.preview.to, this.preview.cc, this.preview.bcc).join(', ');
                        this.$toast.positive(`Email preview was successfully sent to ${recipients}.`);
                    } catch (previewError) {
                        this.$toast.fromError(previewError, 'There was an error while sending the email. Please try again later or contact support.');
                    } finally {
                        this.isLoading = false;
                    }
                }
            },
            makePlaceholders(target) {
                // all matches
                const matches = matchAll(target, placeholderPattern);

                // only unique tokens
                const keys = matches.reduce((memo, match) => ({
                    ...memo,
                    [match[1]]: match,
                }), {});

                return Object.values(keys).map(match => ({
                    value: match[0],
                    token: match[0],
                    label: match[1].split('.').map(_.unCamelCase).join(' '),
                }));
            },
            updatePlaceholder(type, token, placeholder) {
                const item = type.find(data => data.token === token);
                item.value = placeholder;
            },
            enableCopy() {
                this.ccEnabled = true;
            },
            enableBlindCopy() {
                this.bccEnabled = true;
            },
            getCredentialDelegate(...args) {
                return getCredentialDelegate(...args);
            },
        },
    };
</script>
